import { API_BASE_URL } from './config';
import { clearSessionToken } from './nativeSession';
import { createContext, useState, useEffect } from 'react';
import { getDeviceFingerprint } from './deviceFingerprint';
import { installAuthExpiryInterceptor, SESSION_EXPIRED_EVENT } from './apiInterceptor';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated via cookie (from previous session)
    // Note: Token is now stored in httpOnly cookie, not accessible to JavaScript
    fetchUser();
  }, []);

  // See apiInterceptor.js -- clears this context's own state when token expires.
  // Token is in httpOnly cookie, so we only manage user state here.
  useEffect(() => {
    // Kept as a safety net for anything that mounts AuthProvider on its own
    // (tests, Storybook). In the real app index.js has already installed this
    // at import time, and the function no-ops on a second call -- it has to be
    // installed before render, because React runs child effects before parent
    // ones, so App's mount effects would otherwise beat this to the fetch.
    installAuthExpiryInterceptor();
    const onExpired = () => { setUser(null); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const fetchUser = async () => {
    try {
      // SECURITY: credentials: 'include' sends httpOnly auth_token cookie
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    const deviceFingerprint = await getDeviceFingerprint();
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, deviceFingerprint })
    });

    const data = await response.json();
    if (!response.ok && !data.requiresVerification) {
      throw new Error(data.error || 'Registration failed');
    }
    return data; // { requiresVerification: true, email }
  };

  const verifyOtp = async (email, code) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code })
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Verification failed');

    // SECURITY: Token is now in httpOnly cookie, not returned in response
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (email) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to resend code');
    return data;
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to send reset code');
    return data;
  };

  const resetPassword = async (email, code, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code, newPassword })
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to reset password');

    // SECURITY: Token is now in httpOnly cookie, not returned in response
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.error || 'Login failed');
      if (data.requiresVerification) { err.requiresVerification = true; err.email = data.email || email; }
      throw err;
    }

    // Password was right but 2FA is on -- no token issued yet. Signal the
    // caller to collect an authenticator code and call completeTwoFactorLogin.
    if (data.requiresTwoFactor) {
      return { requiresTwoFactor: true };
    }

    // SECURITY: Token is now in httpOnly cookie, not returned in response
    setUser(data.user);
    return data.user;
  };

  // Second step of a 2FA login: re-sends the same credentials plus the TOTP
  // code (matching the backend's stateless /login/2fa -- see routes/auth.js).
  const completeTwoFactorLogin = async (email, password, token) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login/2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, token })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Verification failed');

    // SECURITY: Token is now in httpOnly cookie, not returned in response
    setUser(data.user);
    return data.user;
  };

  const updateProfile = async (fields) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(fields)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Update failed');
    setUser(data.user);
    return data.user;
  };

  const uploadAvatar = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/auth/avatar`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Upload failed');
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    // Best-effort: ends the session server-side (see backend's
    // POST /api/auth/logout) so the token can't keep being used if it leaks,
    // instead of just disappearing from this one browser. Never blocks the
    // actual logout on it -- a network hiccup shouldn't trap someone signed in.
    // SECURITY: Token is in httpOnly cookie, sent automatically with credentials: 'include'
    fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
    // In the app the session is a stored bearer token rather than a cookie the
    // server can expire for us, so it has to be dropped here too -- otherwise
    // signing out would clear the UI and the very next request would re-attach
    // a still-valid token. No-op on the web.
    clearSessionToken();
    setUser(null);
  };

  // There is deliberately no `token` in this value. The JWT lives in an
  // httpOnly auth_token cookie precisely so page JavaScript can't read it,
  // and apiInterceptor attaches credentials to every fetch, so nothing needs
  // one. Components that still destructure `token` get undefined -- harmless
  // inside an Authorization header (the cookie wins server-side) but silently
  // fatal inside a guard: `if (!token) return` is always true and the feature
  // behind it never loads. Use `user` to test "is someone signed in".
  return (
    <AuthContext.Provider value={{ user, loading, register, login, completeTwoFactorLogin, logout, updateProfile, uploadAvatar, verifyOtp, resendOtp, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}
